import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import Glider from 'react-glider'
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { GliderMethods } from 'react-glider/dist/types'
import type { IVideoResultItem, VIDEO_CATEGORIES } from '~/types/video'

import { useDynamicCategoryVideosListQuery } from '../queries'
import VideoThumbnail from '../VideoThumbnail'
import VideoThumbnailSkeleton from '../VideoThumbnail/skeleton'

import { filter, queryConfig } from '~/utils/videos'

import './styles.scss'
import 'glider-js/glider.min.css'

interface Props {
  category: VIDEO_CATEGORIES
  favouriteVideos?: IVideoResultItem[] | null
  onEdit: (video: IVideoResultItem) => void
  onThumbnailClick: (videoUrl: string) => void
  search?: string
  title: string
}

const SLIDES_TO_SHOW = 5

const NextArrow = (): ReactElement => {
  return (
    <button className="glider-next">
      <MdKeyboardArrowRight color="#626262" size={40} />
    </button>
  )
}

const PrevArrow = (): ReactElement => {
  return (
    <button className="glider-prev">
      <MdKeyboardArrowLeft color="#626262" size={40} />
    </button>
  )
}

const VideosByCategory = ({
  category,
  favouriteVideos,
  onEdit,
  onThumbnailClick,
  search,
  title,
}: Props): ReactElement | null => {
  const navigate = useNavigate()

  const nextArrowRef = useRef<HTMLDivElement>(null)
  const prevArrowRef = useRef<HTMLDivElement>(null)
  const gliderRef = useRef<GliderMethods>(null)

  const [videos, setVideos] = useState<IPaginatedResponse<IVideoResultItem>>()

  const { apiFn } = queryConfig[category]

  const { data, isLoading: isVideosLoading } = useDynamicCategoryVideosListQuery(category, apiFn, search)

  const isPaginated = !!(videos?.data && videos.data.length > SLIDES_TO_SHOW)

  const videoByCategoryComponentClassNames = classNames('videos-by-category-component', {
    'with-pagination': isPaginated,
  })

  const isFavourite = (videoId: string): boolean => {
    if (favouriteVideos) {
      return !!favouriteVideos?.find(({ id }) => id === videoId)
    }
    return false
  }

  const handleTitleClick = () => navigate(`/videos/${category}`)

  useEffect(() => {
    if (data) {
      if (data?.data && search) {
        setVideos({
          ...data,
          data: data?.data?.filter(({ tags, title }) => {
            return filter(title, tags, search)
          }),
        })
      } else {
        setVideos(data)
      }
    }
  }, [search, data])

  return !isVideosLoading && !videos?.data?.length ? null : (
    <div className={videoByCategoryComponentClassNames}>
      <div className="title" onClick={handleTitleClick}>
        {title}
      </div>
      {isVideosLoading ? (
        <div className="thumbnails-skeleton-container">
          {[...Array(Math.floor(Math.random() * 4) + 2).keys()].map((__, index) => (
            <VideoThumbnailSkeleton key={index} />
          ))}
        </div>
      ) : (
        <div className="glider-container">
          {isPaginated ? (
            <div ref={prevArrowRef}>
              <PrevArrow />
            </div>
          ) : null}
          <Glider
            arrows={{
              next: nextArrowRef.current,
              prev: prevArrowRef.current,
            }}
            exactWidth={false}
            hasArrows={isPaginated}
            itemWidth={255}
            ref={gliderRef}
            rewind
            skipTrack
            slidesToScroll={5}
            slidesToShow="auto"
          >
            <div className="glider-track">
              {videos?.data?.map((video) => (
                <VideoThumbnail
                  createdAt={video.created_at}
                  createdBy={video.created_by}
                  description={video.description}
                  duration={video.duration}
                  favourite={isFavourite(video.id)}
                  key={`${category}-${video.id}`}
                  onClick={onThumbnailClick}
                  onEdit={() => onEdit(video)}
                  tags={video.tags}
                  thumbnailUrl={video.thumbnail_url}
                  title={video.title}
                  url={video.download_url}
                  videoId={video.id}
                />
              ))}
            </div>
          </Glider>
          {isPaginated ? (
            <div ref={nextArrowRef}>
              <NextArrow />
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default VideosByCategory
